import {RepositoryFilterProvider, type Repository} from '@github-ui/filter/providers'
import type {
  FilterQuery,
  AnyBlock,
  MutableFilterBlock,
  FilterConfig,
  ARIAFilterSuggestion,
  IndexedBlockValueItem,
} from '@github-ui/filter'
import {FilterValueType} from '@github-ui/filter'
import {getUnescapedFilterValue} from '@github-ui/filter/utils'
import {singleValueFilterTypes} from '../SecurityProductsFilterBar'

export class SecurityProductsRepositoryFilterProvider extends RepositoryFilterProvider {
  #orgName: string

  constructor(orgName: string) {
    super({filterTypes: singleValueFilterTypes})

    this.#orgName = orgName
  }

  override async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    _config: FilterConfig,
    caretIndex?: number | null,
  ) {
    return this.processSuggestions(filterQuery, filterBlock, this.#processSuggestion.bind(this), caretIndex)
  }

  #processSuggestion(repo: Repository, query: string): ARIAFilterSuggestion {
    const {name} = repo

    return this.rankSuggestion(query, {
      type: FilterValueType.Value,
      displayName: name,
      ariaLabel: name,
      value: name,
      inlineDescription: false,
      icon: this.icon,
    })
  }

  override async fetchSuggestions(
    value: string,
    block: AnyBlock | MutableFilterBlock,
    filterQuery: FilterQuery,
  ): Promise<Repository[] | null> {
    const orgScopedValue = `${this.#orgName}/${value}`
    return super.fetchSuggestions(orgScopedValue, block, filterQuery)
  }

  override async validateFilterValue(
    value: string,
    fetchParams?: URLSearchParams,
    filterQuery?: FilterQuery,
  ): Promise<Repository | Error | null> {
    const orgScopedValue = `${this.#orgName}/${value}`
    return super.validateFilterValue(orgScopedValue, fetchParams, filterQuery)
  }

  override validateValue(
    _filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    repo: Repository | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!repo || !extractedValue) return false

    return {
      displayName: repo.name,
      value: extractedValue,
    }
  }
}
